/**
 * GRANULLO PLATFORM EVOLUTION — Cascade Engine Context
 * Phase 4: Monitoring + Exposure + Cascade Integration
 * 
 * FOUNDER DIRECTIVE:
 * Cascade Engine must model how risk propagates through the Blueprint Graph.
 * It must use canonical nodes and edges only.
 * 
 * It must evaluate how stress or failure at one node / layer affects:
 * - adjacent nodes
 * - policy dependencies
 * - authority escalation paths
 * - downstream participant clusters
 * - overall program stability
 * 
 * ARCHITECTURE CONSTRAINT: No duplicate topology model may be created.
 * Topology must always derive from the Program Object.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// ============================================================================
// CASCADE TYPES
// ============================================================================

export const CASCADE_SEVERITY = {
  NONE: 'none',
  LOW: 'low',
  MODERATE: 'moderate',
  HIGH: 'high',
  CRITICAL: 'critical'
};

export const PROPAGATION_TYPES = {
  FINANCIAL: 'financial',        // Money flow propagation
  AUTHORITY: 'authority',        // Authority escalation propagation
  POLICY: 'policy',              // Policy dependency propagation
  SIGNAL: 'signal',              // Signal/monitoring propagation
  OPERATIONAL: 'operational'     // Operational dependency propagation
};

// ============================================================================
// CASCADE ENGINE FUNCTIONS
// ============================================================================

/**
 * Calculate cascade risk from a source node
 * Models how failure/stress propagates through the graph
 */
export function calculateCascadeFromNode(sourceNodeId, blueprintGraph, stressLevel = 1.0) {
  if (!blueprintGraph?.nodes || !blueprintGraph?.edges) {
    return { affectedNodes: [], totalImpact: 0, propagationPaths: [] };
  }
  
  const sourceNode = blueprintGraph.nodes.find(n => n.id === sourceNodeId);
  if (!sourceNode) return { affectedNodes: [], totalImpact: 0, propagationPaths: [] };
  
  const visited = new Set([sourceNodeId]);
  const affectedNodes = [];
  const propagationPaths = [];
  
  // BFS to find affected nodes
  const queue = [{ nodeId: sourceNodeId, depth: 0, stress: stressLevel, path: [sourceNodeId] }];
  
  while (queue.length > 0) {
    const { nodeId, depth, stress, path } = queue.shift();
    
    // Find outgoing edges from this node
    const outgoingEdges = blueprintGraph.edges.filter(e => e.source === nodeId);
    
    outgoingEdges.forEach(edge => {
      const targetId = edge.target;
      if (visited.has(targetId)) return;
      
      visited.add(targetId);
      const targetNode = blueprintGraph.nodes.find(n => n.id === targetId);
      if (!targetNode) return;
      
      // Calculate propagated stress (diminishes with distance and edge type)
      const edgeMultiplier = getEdgePropagationMultiplier(edge.type);
      const depthDecay = Math.pow(0.7, depth); // 30% decay per hop
      const propagatedStress = stress * edgeMultiplier * depthDecay;
      
      if (propagatedStress > 0.1) { // Only track significant propagation
        const newPath = [...path, targetId];
        
        affectedNodes.push({
          nodeId: targetId,
          nodeName: targetNode.name,
          nodeType: targetNode.type,
          depth: depth + 1,
          propagatedStress: Math.round(propagatedStress * 100) / 100,
          propagationType: classifyEdgeType(edge.type),
          sourceEdge: edge.id
        });
        
        propagationPaths.push({
          path: newPath,
          totalStress: propagatedStress,
          propagationType: classifyEdgeType(edge.type)
        });
        
        // Continue BFS if stress is still significant
        if (propagatedStress > 0.2 && depth < 5) {
          queue.push({ nodeId: targetId, depth: depth + 1, stress: propagatedStress, path: newPath });
        }
      }
    });
  }
  
  // Calculate total cascade impact
  const totalImpact = affectedNodes.reduce((sum, n) => sum + n.propagatedStress, 0);
  
  return {
    sourceNode: { id: sourceNodeId, name: sourceNode.name, type: sourceNode.type },
    affectedNodes,
    totalImpact: Math.round(totalImpact * 100) / 100,
    propagationPaths,
    maxDepth: Math.max(0, ...affectedNodes.map(n => n.depth)),
    cascadeSeverity: getCascadeSeverity(totalImpact, affectedNodes.length)
  };
}

/**
 * Get propagation multiplier based on edge type
 */
function getEdgePropagationMultiplier(edgeType) {
  const multipliers = {
    financial_flow: 0.9,
    issuance: 0.85,
    distribution: 0.8,
    redemption: 0.7,
    policy_dependency: 0.95,
    authority_escalation: 0.9,
    signal_propagation: 0.6,
    data_flow: 0.5,
    audit_trail: 0.3,
    monitoring: 0.4
  };
  return multipliers[edgeType] || 0.5;
}

/**
 * Classify edge type into propagation category
 */
function classifyEdgeType(edgeType) {
  const classifications = {
    financial_flow: PROPAGATION_TYPES.FINANCIAL,
    issuance: PROPAGATION_TYPES.FINANCIAL,
    distribution: PROPAGATION_TYPES.FINANCIAL,
    redemption: PROPAGATION_TYPES.FINANCIAL,
    policy_dependency: PROPAGATION_TYPES.POLICY,
    authority_escalation: PROPAGATION_TYPES.AUTHORITY,
    signal_propagation: PROPAGATION_TYPES.SIGNAL,
    data_flow: PROPAGATION_TYPES.OPERATIONAL,
    audit_trail: PROPAGATION_TYPES.OPERATIONAL,
    monitoring: PROPAGATION_TYPES.SIGNAL
  };
  return classifications[edgeType] || PROPAGATION_TYPES.OPERATIONAL;
}

/**
 * Determine cascade severity based on impact
 */
function getCascadeSeverity(totalImpact, affectedCount) {
  if (totalImpact >= 3 || affectedCount >= 10) return CASCADE_SEVERITY.CRITICAL;
  if (totalImpact >= 2 || affectedCount >= 7) return CASCADE_SEVERITY.HIGH;
  if (totalImpact >= 1 || affectedCount >= 4) return CASCADE_SEVERITY.MODERATE;
  if (totalImpact >= 0.5 || affectedCount >= 2) return CASCADE_SEVERITY.LOW;
  return CASCADE_SEVERITY.NONE;
}

/**
 * Calculate system-wide cascade risk
 * Evaluates all authority and critical nodes
 */
export function calculateSystemCascadeRisk(blueprintGraph, nodeStressLevels = {}) {
  if (!blueprintGraph?.nodes) {
    return { systemRisk: 0, criticalPaths: [], vulnerableNodes: [] };
  }
  
  // Identify critical nodes (authorities, sponsors, central entities)
  const criticalNodeTypes = ['central_bank', 'regulator', 'sponsor', 'program_sponsor', 'authority_node'];
  const criticalNodes = blueprintGraph.nodes.filter(n => criticalNodeTypes.includes(n.type));
  
  // Calculate cascade from each stressed or critical node
  const cascadeResults = [];
  const vulnerableNodes = new Map();
  
  // Check explicitly stressed nodes
  Object.entries(nodeStressLevels).forEach(([nodeId, stressLevel]) => {
    if (stressLevel > 0.25) {
      const cascade = calculateCascadeFromNode(nodeId, blueprintGraph, stressLevel);
      cascadeResults.push(cascade);
      
      cascade.affectedNodes.forEach(affected => {
        const existing = vulnerableNodes.get(affected.nodeId);
        if (!existing || existing.propagatedStress < affected.propagatedStress) {
          vulnerableNodes.set(affected.nodeId, affected);
        }
      });
    }
  });
  
  // Also evaluate critical nodes with assumed minimal stress
  criticalNodes.forEach(node => {
    if (!nodeStressLevels[node.id]) {
      const cascade = calculateCascadeFromNode(node.id, blueprintGraph, 0.3);
      if (cascade.affectedNodes.length >= 3) {
        cascadeResults.push({ ...cascade, isHypothetical: true });
      }
    }
  });
  
  // Calculate overall system risk
  const totalRisk = cascadeResults.reduce((sum, c) => sum + c.totalImpact, 0);
  const normalizedRisk = Math.min(100, Math.round(totalRisk * 20));
  
  // Identify critical propagation paths
  const criticalPaths = cascadeResults
    .flatMap(c => c.propagationPaths)
    .filter(p => p.totalStress >= 0.5)
    .sort((a, b) => b.totalStress - a.totalStress)
    .slice(0, 10);
  
  return {
    systemRisk: normalizedRisk,
    cascadeResults,
    criticalPaths,
    vulnerableNodes: Array.from(vulnerableNodes.values()),
    criticalNodeCount: criticalNodes.length,
    severity: getCascadeSeverity(totalRisk, vulnerableNodes.size)
  };
}

/**
 * Identify authority bottlenecks
 * Nodes that many paths depend on
 */
export function identifyAuthorityBottlenecks(blueprintGraph) {
  if (!blueprintGraph?.nodes || !blueprintGraph?.edges) return [];
  
  const incomingCount = {};
  const outgoingCount = {};
  
  blueprintGraph.edges.forEach(edge => {
    incomingCount[edge.target] = (incomingCount[edge.target] || 0) + 1;
    outgoingCount[edge.source] = (outgoingCount[edge.source] || 0) + 1;
  });
  
  // Bottlenecks are nodes with high incoming AND outgoing connections
  const bottlenecks = blueprintGraph.nodes
    .map(node => ({
      nodeId: node.id,
      nodeName: node.name,
      nodeType: node.type,
      incoming: incomingCount[node.id] || 0,
      outgoing: outgoingCount[node.id] || 0,
      total: (incomingCount[node.id] || 0) + (outgoingCount[node.id] || 0),
      bottleneckScore: Math.sqrt((incomingCount[node.id] || 0) * (outgoingCount[node.id] || 0))
    }))
    .filter(n => n.bottleneckScore > 1)
    .sort((a, b) => b.bottleneckScore - a.bottleneckScore);
  
  return bottlenecks;
}

// ============================================================================
// CONTEXT
// ============================================================================

const CascadeEngineContext = createContext(null);

export function CascadeEngineProvider({ children }) {
  // Cascade state
  const [cascadeAnalysis, setCascadeAnalysis] = useState(null);
  const [systemRisk, setSystemRisk] = useState({ systemRisk: 0, severity: CASCADE_SEVERITY.NONE });
  const [bottlenecks, setBottlenecks] = useState([]);
  const [lastAnalyzedAt, setLastAnalyzedAt] = useState(null);
  
  // Blueprint reference (set by integration)
  const [blueprintGraph, setBlueprintGraph] = useState(null);
  
  /**
   * Set blueprint graph for cascade analysis
   */
  const setBlueprint = useCallback((blueprint) => {
    setBlueprintGraph(blueprint);
    if (blueprint) {
      // Auto-identify bottlenecks
      const newBottlenecks = identifyAuthorityBottlenecks(blueprint);
      setBottlenecks(newBottlenecks);
    }
  }, []);
  
  /**
   * Analyze cascade from a specific node
   */
  const analyzeCascadeFromNode = useCallback((nodeId, stressLevel = 1.0) => {
    if (!blueprintGraph) return null;
    
    const analysis = calculateCascadeFromNode(nodeId, blueprintGraph, stressLevel);
    setCascadeAnalysis(analysis);
    setLastAnalyzedAt(new Date().toISOString());
    return analysis;
  }, [blueprintGraph]);
  
  /**
   * Analyze system-wide cascade risk
   */
  const analyzeSystemRisk = useCallback((nodeStressLevels = {}) => {
    if (!blueprintGraph) return null;
    
    const risk = calculateSystemCascadeRisk(blueprintGraph, nodeStressLevels);
    setSystemRisk(risk);
    setLastAnalyzedAt(new Date().toISOString());
    return risk;
  }, [blueprintGraph]);
  
  /**
   * Quick cascade impact estimate
   */
  const estimateCascadeImpact = useCallback((nodeId) => {
    if (!blueprintGraph) return { impact: 'unknown', affectedCount: 0 };
    
    const cascade = calculateCascadeFromNode(nodeId, blueprintGraph, 1.0);
    return {
      impact: cascade.cascadeSeverity,
      affectedCount: cascade.affectedNodes.length,
      maxDepth: cascade.maxDepth
    };
  }, [blueprintGraph]);
  
  // Computed summary
  const cascadeSummary = useMemo(() => ({
    systemRiskScore: systemRisk.systemRisk,
    systemRiskSeverity: systemRisk.severity,
    bottleneckCount: bottlenecks.length,
    topBottleneck: bottlenecks[0] || null,
    criticalPathCount: systemRisk.criticalPaths?.length || 0,
    vulnerableNodeCount: systemRisk.vulnerableNodes?.length || 0,
    lastAnalyzedAt
  }), [systemRisk, bottlenecks, lastAnalyzedAt]);
  
  const value = {
    // Constants
    CASCADE_SEVERITY,
    PROPAGATION_TYPES,
    
    // State
    cascadeAnalysis,
    systemRisk,
    bottlenecks,
    blueprintGraph,
    
    // Computed
    cascadeSummary,
    
    // Actions
    setBlueprint,
    analyzeCascadeFromNode,
    analyzeSystemRisk,
    estimateCascadeImpact,
    
    // Utility functions
    calculateCascadeFromNode,
    calculateSystemCascadeRisk,
    identifyAuthorityBottlenecks
  };
  
  return (
    <CascadeEngineContext.Provider value={value}>
      {children}
    </CascadeEngineContext.Provider>
  );
}

export function useCascadeEngine() {
  const context = useContext(CascadeEngineContext);
  if (!context) {
    throw new Error('useCascadeEngine must be used within CascadeEngineProvider');
  }
  return context;
}

export default CascadeEngineContext;
